import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import LaunchIcon from '@mui/icons-material/Launch'

import CalendarIcon from '@/components/ui/icons/Calendar'
import { OfflineConsultation } from '@/core/offline/types/database.types'
import colors from '@/assets/scss/_themes-vars.module.scss'

type OfflineConsultationsCardViewProps = {
  consultations: OfflineConsultation[]
  onOpen?: (consultation: OfflineConsultation) => void
}

export function OfflineConsultationsCardView(props: OfflineConsultationsCardViewProps) {
  const { consultations, onOpen } = props
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      {consultations.map((consultation) => {
        const patient = typeof consultation.patient === 'string' ? { userInfo: { name: 'Unknown', surname: 'Patient', gender: '' } } : consultation.patient

        return (
          <Card key={consultation._offlineId || consultation._id} sx={{ position: 'relative' }}>
            <CardContent>
              <Stack spacing={2}>
                {/* Patient Info */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {patient.userInfo.surname}
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.gray600 }}>
                    {patient.userInfo.name}
                  </Typography>
                  {patient.userInfo.gender && (
                    <Chip color={getGenderColor(patient.userInfo.gender) as any} label={patient.userInfo.gender} size="small" variant="filled" />
                  )}
                </Stack>

                {/* Date and Status */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarIcon fontSize="small" />
                    <Chip
                      color="secondary"
                      label={
                        <span>
                          {dayjs(consultation.startDate).isSame(dayjs(), 'day')
                            ? t(`dayjs:calendar:today`)
                            : dayjs(consultation.startDate).format('DD/MM/YYYY')}{' '}
                          - <b>{dayjs(consultation.startDate).format('HH[h]mm')}</b>
                        </span>
                      }
                      variant="filled"
                      size="small"
                    />
                  </Stack>
                  <Chip
                    color={getStatusColor(consultation.status) as any}
                    label={t(`common:model.consultation.status.${consultation.status}.label`)}
                    size="small"
                    variant="filled"
                  />
                </Stack>

                {/* Sync Status */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Chip
                    color={consultation._syncStatus === 'synced' ? 'success' : 'warning'}
                    label={consultation._syncStatus === 'synced' ? t('Synced') : t('Pending sync')}
                    size="small"
                    variant="outlined"
                  />
                  <IconButton color="primary" onClick={() => onOpen?.(consultation)} size="small">
                    <LaunchIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )
      })}
    </Stack>
  )
}

function getStatusColor(status: string) {
  switch (status) {
    case 'scheduled':
      return 'primary'
    case 'accepted':
    case 'active':
      return 'success'
    case 'canceled':
    case 'refused':
      return 'error'
    case 'completed':
      return 'info'
    default:
      return 'default'
  }
}

function getGenderColor(gender: string) {
  switch (gender) {
    case 'M':
      return 'primary'
    case 'F':
      return 'secondary'
    default:
      return 'info'
  }
}
